/**
 * web/src/lib/clientErrors.ts — the client half of error visibility
 * (card bdsxKQG9HrOQ, epic wgoMcHSuyGfy part 2/3).
 *
 * Before this the client had no capture at all: a render crash left a blank
 * velvet page and the 2026-10-24 outage showed up as API failures with nothing
 * recording them from this side. Three sources feed ONE reporter:
 *
 *   - `window` 'error'            (the window.onerror hook)
 *   - `window` 'unhandledrejection'
 *   - an ErrorBoundary's componentDidCatch
 *
 * Rules that make it safe to call from a crash path:
 *
 *   1. It NEVER throws. Every statement is inside a try/catch, because an
 *      error reporter that throws is a second outage.
 *   2. It NEVER goes through lib/api.ts. `api()` throws on a non-2xx, so a 429
 *      from this very route would re-enter the reporter and loop. Raw fetch,
 *      response ignored.
 *   3. It dedupes on message+route per session and stops at 20 posts, so a
 *      render loop posts once instead of becoming a logging denial of service.
 *   4. It carries no PII: the route is a pathname (never a query string —
 *      `?ref=`, `?code=` and emailed tokens live there), addresses are
 *      redacted before the body is built, and the message is capped.
 *
 * It posts when the device holds a couple token OR a signed-in account
 * session (jasRGjQeuBra): the route
 * answers 401 without one (see worker/src/api/client-errors.ts), so a
 * signed-out shell crash renders the apology but is not reported. That gap is
 * recorded on the card as a follow-up, not silently absorbed here.
 */
import { API_BASE, getAuthSession, getToken } from './api.ts';

export type ClientErrorKind = 'onerror' | 'unhandledrejection' | 'boundary' | 'panel';

/** Posts per session — the client's own sample ceiling. */
const MAX_POSTS = 20;
const MAX_MESSAGE = 300;
const MAX_STACK_HEAD = 800;

/** Anything address-shaped is redacted on the client AND again on the server,
 *  so a stack frame quoting a login form never leaves the device. */
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

/** message+route already reported this session. */
const seen = new Set<string>();
let posted = 0;

/** The build this bundle is, for triage ("is it only on the old build?"). */
export function currentBuildId(): string {
  try {
    return typeof __BUILD_ID__ === 'string' && __BUILD_ID__ ? __BUILD_ID__ : 'dev';
  } catch {
    return 'dev';
  }
}

/** Pathname only. The app has no client router, so this is `/` for the whole
 *  shell — the identifying half of the payload is the message, not the URL. */
function currentRoute(): string {
  try {
    return window.location.pathname || '/';
  } catch {
    return '/';
  }
}

function redact(input: unknown): string {
  return String(input ?? '').replace(EMAIL_RE, '[redacted]');
}

function cap(input: unknown, max: number): string {
  return redact(input).slice(0, max);
}

/**
 * Report one client error. Returns true when a POST was actually issued, so a
 * probe can count them without sniffing the network.
 */
export function reportClientError(
  kind: ClientErrorKind,
  message: unknown,
  stack?: unknown,
): boolean {
  try {
    const route = currentRoute();
    const text = cap(message, MAX_MESSAGE) || 'unknown error';
    const key = `${text}|${route}`;
    if (seen.has(key)) return false;   // a render loop posts once
    if (posted >= MAX_POSTS) return false;
    // The route accepts two credentials (jasRGjQeuBra): the couple token, or
    // the account session a signed-in device holds before any pairing exists —
    // which is exactly the Pair screen, the moment most worth reporting. With
    // neither it 401s, so an anonymous device spends no quota on a POST that
    // cannot be accepted.
    const token = getToken() ?? getAuthSession()?.token;
    if (!token) return false;

    seen.add(key);
    posted += 1;

    const body = JSON.stringify({
      message: text,
      stack_head: cap(stack, MAX_STACK_HEAD),
      route,
      buildId: currentBuildId(),
      kind,
    });

    // keepalive: a crash during navigation still gets the bytes out. The
    // promise is deliberately not awaited and never has a .catch that could
    // itself reject — the fetch is wrapped synchronously.
    void fetch(`${API_BASE}/api/errors/client`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => undefined);
    return true;
  } catch {
    return false;
  }
}

/** Install the two window hooks exactly once. Called from main.tsx before
 *  React mounts, so a crash during the first render is already covered. */
export function installClientErrorHandlers(): void {
  try {
    const w = window as unknown as { __pearErrorHandlers?: boolean };
    if (w.__pearErrorHandlers) return;
    w.__pearErrorHandlers = true;

    // addEventListener rather than assigning `window.onerror`: the 'error'
    // event IS the onerror hook, and registering it leaves any handler another
    // library installed free to run. Resource load errors carry no message —
    // they are not ours to report.
    window.addEventListener('error', (event) => {
      const message = (event as ErrorEvent).message;
      if (typeof message !== 'string' || !message) return;
      const err = (event as ErrorEvent).error;
      reportClientError('onerror', message, err instanceof Error ? err.stack : undefined);
    });

    window.addEventListener('unhandledrejection', (event) => {
      const reason = (event as PromiseRejectionEvent).reason;
      reportClientError(
        'unhandledrejection',
        reason instanceof Error ? reason.message : String(reason),
        reason instanceof Error ? reason.stack : undefined,
      );
    });
  } catch {
    /* installing the reporter must never break the boot it exists to watch */
  }
}

/** Test seam: forget the session's dedupe set and counter. Not used by the
 *  app; a probe that needs a clean slate calls this instead of reloading. */
export function resetClientErrorStateForTests(): void {
  seen.clear();
  posted = 0;
}

/** How many posts this session has issued — for probes and for a future
 *  Settings → Build diagnostic line. */
export function clientErrorPostCount(): number {
  return posted;
}
